<template>
  <f7-page name="group-buy-campaign">
    <f7-navbar :title="$t('groupBuy.campaign')" back-link />
    <AppLayout>
      <template #main>
        <f7-block v-if="loading" class="gbd-loading">
          <f7-preloader />
        </f7-block>

        <template v-else-if="campaign">
          <!-- Hero Card with Status & Escrow -->
          <f7-card class="gbd-hero" :padding="false">
            <f7-card-content class="gbd-hero-content" :padding="false">
              <h1 class="gbd-title">{{ campaign.title }}</h1>
              <p v-if="campaign.description" class="gbd-desc">{{ campaign.description }}</p>
              <div class="gbd-status-row display-flex align-items-center flex-wrap gap-2">
                <f7-badge :style="{ color: statusMeta.color, background: statusBg }" class="font-label-sm font-semibold">{{ statusLabel }}</f7-badge>
                <span v-if="campaign.escrowRequired" class="gbd-escrow display-flex align-items-center gap-1">
                  <span class="material-symbols-outlined shield-heart-icon">shield_with_heart</span>
                  {{ t("groupBuy.escrowRequired") }}
                </span>
              </div>

              <f7-progressbar :progress="progress" class="mt-3" />
              <div class="gbd-stats display-flex justify-content-space-around text-align-center mt-3">
                <div class="gbd-stat display-flex flex-direction-column">
                  <strong class="font-headline-md text-primary">{{ participantCount }}</strong>
                  <span class="font-label-sm text-on-surface-variant">{{ t("groupBuy.participants") }}</span>
                </div>
                <div class="gbd-stat display-flex flex-direction-column">
                  <strong class="font-headline-md">{{ campaign.maximumParticipants }}</strong>
                  <span class="font-label-sm text-on-surface-variant">{{ t("groupBuy.maxParticipants") }}</span>
                </div>
                <div class="gbd-stat display-flex flex-direction-column">
                  <strong class="font-headline-md">{{ remaining }}</strong>
                  <span class="font-label-sm text-on-surface-variant">{{ t("groupBuy.remaining") }}</span>
                </div>
              </div>

              <p class="gbd-price display-flex align-items-baseline gap-1 mt-3">
                {{ t("groupBuy.currentPrice") }}
                <span class="font-headline-md text-primary">{{ currentPrice }}</span>
              </p>
              <p class="gbd-unit-price font-label-sm text-on-surface-variant mt-1">
                {{ t("groupBuy.startingPrice") }} {{ fmtCfa(campaign.startingPrice) }}
              </p>

              <!-- Next Tier Teaser -->
              <div v-if="nextTier" class="next-tier-teaser mt-3 p-3 rounded-lg bg-secondary-container/20 display-flex align-items-center justify-content-between">
                <div class="display-flex align-items-center gap-2 text-on-secondary-container">
                  <f7-icon material="trending_down" :size="18" class="text-secondary" />
                  <span class="font-label-md text-label-md">
                    {{ t('groupBuy.nextTierTeaser', { price: fmtCfa(nextTier.unitPrice), n: nextTier.minimumParticipants }) }}
                  </span>
                </div>
                <span class="font-label-sm text-label-sm text-secondary font-bold">{{ unlockedPct(nextTier) }}</span>
              </div>
            </f7-card-content>
          </f7-card>

          <!-- Tier Matrix (Group Pricing Scale) -->
          <f7-card class="tier-matrix-card" :padding="false">
            <f7-card-content class="tier-matrix-content" :padding="false">
              <div class="display-flex align-items-center justify-content-space-between mb-3">
                <div class="display-flex align-items-center gap-2">
                  <f7-icon material="stacked_bar_chart" :size="20" class="text-primary" />
                  <h2 class="font-headline-sm text-on-surface">{{ t('groupBuy.tierMatrixTitle') }}</h2>
                </div>
                <f7-badge class="font-label-sm font-medium bg-primary/10 text-primary">{{ t('groupBuy.wholesaleContract') }}</f7-badge>
              </div>

              <div class="tier-matrix grid grid-cols-3 gap-2 text-center">
                <template v-for="(tier, i) in tierRows" :key="tier.id || `${tier.minimumParticipants}-${tier.unitPrice}`">
                  <f7-card class="tier-card" :padding="false"
                    :class="{
                      'bg-primary-fixed/25 shadow-sm': unlocked(tier),
                      'bg-surface-container-low opacity-70': !unlocked(tier) && i === 0,
                      'bg-surface-container-low opacity-80': !unlocked(tier) && i > 0,
                    }"
                  >
                    <f7-card-content class="tier-card-content" :padding="false">
                      <span class="font-label-sm text-label-sm text-on-surface-variant">{{ t('groupBuy.tierPalier', { n: i + 1 }) }}</span>
                      <span class="font-label-md text-label-md font-bold text-on-surface">{{ tier.minimumParticipants }}{{ tier.maximumParticipants ? `-${tier.maximumParticipants}` : '+' }}</span>
                      <span class="font-label-sm text-label-sm text-outline">{{ t('groupBuy.buyers', { count: tier.minimumParticipants }) }}</span>
                      <div class="w-full h-0.5 bg-outline-variant my-1"></div>
                      <span class="font-label-md text-label-md font-semibold" :class="{ 'text-primary': unlocked(tier), 'text-on-surface': !unlocked(tier) }">{{ fmtCfa(tier.unitPrice) }}</span>
                      <span class="tier-savings font-label-sm" :class="{ 'text-secondary font-bold': unlocked(tier), 'text-outline': !unlocked(tier) }">
                        {{ tierSavings(tier) }}
                      </span>
                      <f7-badge v-if="unlocked(tier)" class="tier-active-badge font-label-sm bg-primary text-on-primary rounded-bl">{{ t('groupBuy.tierActive') }}</f7-badge>
                    </f7-card-content>
                  </f7-card>
                </template>
              </div>

              <div class="tier-footnote mt-3 p-2 rounded-lg bg-surface-container/50 display-flex align-items-center gap-2 text-on-surface-variant">
                <f7-icon material="verified_user" :size="16" class="text-primary" />
                <p class="font-body-sm leading-tight tier-footnote-text">{{ t('groupBuy.tierFootnote') }}</p>
              </div>
            </f7-card-content>
          </f7-card>

          <!-- Trust Strip -->
          <f7-card class="trust-strip" :padding="false">
            <f7-card-content class="trust-strip-content" :padding="false">
              <f7-icon material="lock_clock" :size="24" class="text-primary flex-shrink-0 mt-0.5" />
              <div class="flex flex-col gap-1">
                <h4 class="font-label-md text-label-md text-on-surface font-semibold">{{ t('groupBuy.trustTitle') }}</h4>
                <p class="font-body-sm text-body-sm text-on-surface-variant">{{ t('groupBuy.trustText') }}</p>
              </div>
            </f7-card-content>
          </f7-card>

          <!-- Creator Lifecycle Actions -->
          <f7-card v-if="isCreator" class="stb-lifecycle" :padding="false">
            <f7-card-content class="stb-lifecycle-content" :padding="false">
              <f7-block-title>{{ t("groupBuy.manageCampaign") }}</f7-block-title>
              <div class="gbd-space-y display-flex flex-direction-column gap-2">
                <f7-button v-if="status === 'ACTIVE'" fill raised round large @click="doTargetReached">{{ t("groupBuy.markTargetReached") }}</f7-button>
                <f7-button v-if="status === 'TARGET_REACHED'" raised round  fill large @click="doAllocate">{{ t("groupBuy.startAllocation") }}</f7-button>
                <f7-button v-if="status === 'ALLOCATING'" raised round  fill large @click="doSuccess">{{ t("groupBuy.markSuccessful") }}</f7-button>
                <f7-button v-if="status === 'ALLOCATING'" raised round  outline large color="red" @click="doFail">{{ t("groupBuy.markFailed") }}</f7-button>
                <f7-button v-if="['ACTIVE', 'TARGET_REACHED'].includes(status)" raised round  outline large color="red" @click="doCancel">{{ t("groupBuy.cancelCampaign") }}</f7-button>
              </div>
            </f7-card-content>
          </f7-card>

          <!-- Tier Editor for Creator -->
          <f7-card v-if="isCreator && !['SUCCESSFUL', 'FAILED', 'CANCELLED', 'ALLOCATING'].includes(status)" :padding="false">
            <f7-card-content class="gbd-tier-editor-content" :padding="false">
              <f7-block-title>{{ t("groupBuy.tiers") }}</f7-block-title>
              <f7-list inset strong>
                <f7-list-item v-for="tier in tierRows" :key="tier.id || `${tier.minimumParticipants}-${tier.unitPrice}`" :title="t('groupBuy.tierMinBuyers', { n: tier.minimumParticipants })" :subtitle="tierSub(tier)">
                  <template #after>
                    <span class="gbd-tier-price">{{ fmtCfa(tier.unitPrice) }}</span>
                    <f7-badge :color="unlocked(tier) ? 'green' : 'gray'">{{ unlocked(tier) ? t("groupBuy.unlocked") : t("groupBuy.locked") }}</f7-badge>
                    <f7-button v-if="tier.hasServerId" outline raised round small class="gbd-tier-remove" @click="askDeleteTier(tier)">{{ t("groupBuy.removeTier") }}</f7-button>
                  </template>
                </f7-list-item>
                <f7-list-item title="+" link @click="openAddTier"><span class="gbd-add-tier">{{ t("groupBuy.addTier") }}</span></f7-list-item>
              </f7-list>
            </f7-card-content>
          </f7-card>
        </template>

        <f7-block v-else>
          <p class="gbd-empty">{{ t("groupBuy.campaignNotFound") }}</p>
        </f7-block>
      </template>
    </AppLayout>

    <AdaptiveSheet v-model:opened="addTierOpen" :swipe-to-close="true" :tablet-fullscreen="false">
      <f7-page-content>
        <f7-block-title class="gbd-sheet-title">{{ t("groupBuy.addTier") }}</f7-block-title>
        <f7-list inset strong form>
          <f7-list-input v-model:value="tierForm.minimumParticipants" type="number" :label="t('groupBuy.tierMinBuyers')" />
          <f7-list-input v-model:value="tierForm.unitPrice" type="number" :label="t('groupBuy.tierUnitPrice')" />
        </f7-list>
        <f7-block>
          <f7-button fill large raised round  :disabled="!canAddTier" @click="addTier">{{ t("groupBuy.saveTier") }}</f7-button>
        </f7-block>
      </f7-page-content>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import AdaptiveSheet from "../components/common/AdaptiveSheet.vue";
import { fmtCfa } from "../constants/listing-options.js";
import {
  useGroupBuyStore,
  GROUP_BUY_STATUS_META,
  groupBuyUnitPrice,
} from "../js/pinia-stores/groupBuy.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import i18n from "../js/i18n.js";

const props = defineProps({
  groupId: { type: String, required: true },
  campaignId: { type: String, required: true },
});

const { t } = i18n.global;
const groupBuyStore = useGroupBuyStore();
const keycloakStore = useKeycloakStore();
const groupsStore = useGroupsStore();

const loading = ref(true);
const addTierOpen = ref(false);
const tierForm = reactive({ minimumParticipants: 2, unitPrice: null });

const me = computed(() => keycloakStore.subject || "");
const campaign = computed(() => groupBuyStore.activeCampaign);
const status = computed(() => campaign.value?.status);
const isCreator = computed(
  () =>
    campaign.value &&
    String(campaign.value.creatorUserId) === String(me.value),
);

const statusMeta = computed(
  () => GROUP_BUY_STATUS_META[status.value] || GROUP_BUY_STATUS_META.DRAFT,
);
const statusLabel = computed(() => t(statusMeta.value.labelKey));
const statusBg = computed(() => colorMix(statusMeta.value.color, 12));

const participantCount = computed(() =>
  Number(campaign.value?.currentParticipantsCount ?? campaign.value?.participantCount ?? 0),
);
const progress = computed(() =>
  Math.min(
    (participantCount.value /
      Number(campaign.value?.maximumParticipants || 1)) *
      100,
    100,
  ),
);
const remaining = computed(() => {
  if (participantCount.value >= Number(campaign.value?.maximumParticipants)) {
    return t("groupBuy.full");
  }
  return Number(campaign.value?.maximumParticipants) - participantCount.value;
});
const currentPrice = computed(() => {
  const unit = groupBuyUnitPrice(tierRows.value, participantCount.value);
  return unit ? fmtCfa(unit) : fmtCfa(campaign.value?.startingPrice);
});
const tierRows = computed(() => {
  if (groupBuyStore.tiers.length) {
    return groupBuyStore.tiers.map((tier) => ({
      ...tier,
      hasServerId: true,
    }));
  }
  return campaign.value?.tiers || [];
});

// New: Next tier for teaser
const nextTier = computed(() => {
  const sorted = [...tierRows.value].sort((a, b) => Number(a.minimumParticipants) - Number(b.minimumParticipants));
  return sorted.find(t => !unlocked(t)) || sorted[sorted.length - 1];
});

const unlockedPct = (tier) => {
  if (!campaign.value?.startingPrice) return '';
  const pct = Math.round((1 - Number(tier.unitPrice) / Number(campaign.value.startingPrice)) * 100);
  return pct > 0 ? `-${pct}%` : '';
};

const tierSavings = (tier) => {
  if (!campaign.value?.startingPrice) return '';
  const saving = Number(campaign.value.startingPrice) - Number(tier.unitPrice);
  return saving > 0 ? `${t('groupBuy.savings', { amount: fmtCfa(saving) })}` : '';
};

const canAddTier = computed(
  () => Number(tierForm.minimumParticipants) > 0 && Number(tierForm.unitPrice) > 0,
);

function unlocked(tier) {
  return participantCount.value >= Number(tier.minimumParticipants);
}
function tierSub(tier) {
  return t("groupBuy.tierReaches", { n: tier.minimumParticipants });
}
function colorMix(hex, alpha) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
}

onMounted(async () => {
  await Promise.all([
    groupsStore.fetchGroupDetail(props.groupId).catch(() => {}),
  ]);
  try {
    await groupBuyStore.getCampaign(props.campaignId);
    await groupBuyStore.getTiers(props.campaignId);
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("groupBuy.errorGeneric"),
    );
  }
  loading.value = false;
});

function openAddTier() {
  tierForm.minimumParticipants =
    (tierRows.value.length
      ? Number(tierRows.value[tierRows.value.length - 1].minimumParticipants)
      : 0) + 1;
  tierForm.unitPrice = Number(campaign.value?.startingPrice) || 0;
  addTierOpen.value = true;
}

async function addTier() {
  if (!canAddTier.value) return;
  addTierOpen.value = false;
  f7.preloader.show();
  try {
    await groupBuyStore.addTier({
      campaignId: props.campaignId,
      minimumParticipants: Number(tierForm.minimumParticipants),
      unitPrice: Number(tierForm.unitPrice),
      sortOrder: (tierRows.value.length || 0) + 1,
    });
    await groupBuyStore.getTiers(props.campaignId);
    f7.toast.show({ text: t("groupBuy.tierSaved"), closeTimeout: 1800 });
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("groupBuy.errorGeneric"),
    );
  } finally {
    f7.preloader.hide();
  }
}

function askDeleteTier(tier) {
  if (!tier.hasServerId) return;
  f7.dialog.confirm(
    t("groupBuy.deleteTierConfirm"),
    t("groupBuy.removeTier"),
    async () => {
      f7.preloader.show();
      try {
        await groupBuyStore.deleteTier(tier.id);
        f7.toast.show({ text: t("groupBuy.tierDeleted"), closeTimeout: 1800 });
      } catch (error) {
        f7.dialog.alert(
          error?.response?.data?.message || t("groupBuy.errorGeneric"),
        );
      } finally {
        f7.preloader.hide();
      }
    },
  );
}

async function launch() {
  await runAction(() => groupBuyStore.activateCampaign(props.campaignId));
}
async function doTargetReached() {
  await runAction(() => groupBuyStore.markTargetReached(props.campaignId));
}
async function doAllocate() {
  await runAction(() => groupBuyStore.startAllocation(props.campaignId));
}
async function doSuccess() {
  await runAction(() => groupBuyStore.markCampaignSuccessful(props.campaignId));
}
async function doFail() {
  await runAction(() => groupBuyStore.markCampaignFailed(props.campaignId));
}
async function doCancel() {
  await runAction(() => groupBuyStore.cancelCampaign(props.campaignId));
}

async function runAction(fn) {
  f7.preloader.show();
  try {
    await fn();
    await groupBuyStore.getTiers(props.campaignId).catch(() => {});
    f7.toast.show({ text: t("groupBuy.campaignUpdated"), closeTimeout: 2000 });
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("groupBuy.errorGeneric"),
    );
  } finally {
    f7.preloader.hide();
  }
}
</script>

<style scoped>
.gbd-loading {
  text-align: center;
  padding: 24px 0;
}
.gbd-hero {
  margin: 0;
  box-shadow: none;
  border-radius: 16px;
  background: var(--f7-list-bg-color, #f5f5f5);
  border: 1px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
}
.gbd-hero-content {
  position: relative;
  padding: 16px;
}
.gbd-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px;
}
.gbd-desc {
  font-size: 13px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 0 0 12px;
}
.gbd-status-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.gbd-escrow {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  color: var(--f7-theme-color);
}
.shield-heart-icon {
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 12;
}
.gbd-stats {
  display: flex;
  justify-content: space-around;
  text-align: center;
  margin: 16px 0 12px;
}
.gbd-stat {
  display: flex;
  flex-direction: column;
}
.gbd-stat strong {
  font-size: 20px;
  font-weight: 700;
  color: var(--f7-text-color, #161d18);
}
.gbd-stat span {
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 2px;
}
.gbd-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 0;
}
.gbd-price span {
  font-size: 22px;
  font-weight: 700;
  color: var(--f7-theme-color);
}
.gbd-unit-price {
  font-size: 12.5px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 4px 0 0;
}
.next-tier-teaser {
  border-radius: 12px;
}
.tier-matrix-card {
  margin: 0;
  box-shadow: none;
  border-radius: 16px;
}
.tier-matrix-content {
  position: relative;
  padding: 16px;
}
.tier-matrix {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.tier-card {
  margin: 0;
  box-shadow: none;
  border-radius: 12px;
  transition: all 0.2s ease;
}
.tier-card-content {
  position: relative;
  padding: 12px 8px;
}
.tier-savings {
  font-size: 10px;
}
.tier-active-badge {
  position: absolute;
  top: 0;
  right: 0;
  min-width: 0;
  padding: 2px 6px;
  border-radius: 0 0 0 9999px;
}
.tier-footnote {
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  padding: 8px 12px;
}
.tier-footnote-text {
  font-size: 12px;
}
.trust-strip {
  margin: 16px 0 0;
  box-shadow: none;
  border-radius: 12px;
  background: var(--f7-list-bg-color, #f5f5f5);
  border: 1px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
}
.trust-strip-content {
  position: relative;
  padding: 16px;
}
.gbd-tier-price {
  font-size: 13px;
  font-weight: 600;
  margin-right: 8px;
  color: var(--f7-theme-color);
}
.gbd-tier-remove {
  --f7-button-outline-border-color: #c0392b;
  --f7-button-text-color: #c0392b;
  font-size: 12px;
  font-weight: 600;
  margin-left: 8px;
}
.gbd-add-tier {
  color: var(--f7-theme-color);
  font-weight: 600;
  font-size: 13px;
}
.gbd-space-y {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.stb-lifecycle {
  margin: 0;
  box-shadow: none;
  border-radius: 16px;
}
.stb-lifecycle-content {
  position: relative;
  padding: 16px;
}
.gbd-tier-editor-content {
  position: relative;
  padding: 16px;
}
.gbd-empty {
  font-size: 13px;
  color: var(--f7-on-surface-variant, #8e8e93);
  padding: 24px 16px;
}
.gbd-sheet-title {
  text-align: center;
}
</style>